// Operator-facing alerts inbox. Gmail-style thread-grouped layout:
//   - left pane: list of THREADS (alerts sharing a thread_key collapse
//     into one row showing subject + msg count + last-activity time)
//   - right pane: full thread shown chronologically (oldest at top,
//     newest at bottom), each message as its own card with the agent's
//     emoji + name + body. Action buttons (Execute, Resolve) on the
//     LATEST message in the thread.
//   - selection lives in URL (?id=<alertId> auto-finds the thread;
//     ?thread=<key> selects the thread directly)
//   - alerts without a thread_key render as their own 1-message thread
//
// On narrow screens the right pane stacks below the list.
//
// Toggle "Show resolved" includes resolved alerts. A thread is shown
// as long as at least ONE of its messages matches the filter; this
// matches Gmail's behavior where a thread containing one unread
// message stays in the inbox even if the rest are read.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { alertsApi, optimizeApi, type Alert, type ActiveCooldown } from "../lib/api";
import { MarkdownBody } from "../components/MarkdownBody";

interface Thread {
  /** Stable key — alert.threadKey or fallback to alert.dedupKey for
   *  ungrouped alerts. Used for ?thread=<key> URL state. */
  key: string;
  /** Subject line — title of the FIRST (chronologically) message. */
  subject: string;
  /** Human-readable category used in the list pane (e.g. "content_brief"). */
  category: string;
  /** Domain shown in the list pane when thread is site-scoped. */
  domain: string | null;
  siteId: string | null;
  /** All messages in the thread, oldest first. */
  messages: Alert[];
  /** Most-recent activity time. */
  lastActivity: string;
  /** Highest severity across messages — drives the bar color. */
  severity: "critical" | "warn" | "info";
  /** Has at least one unresolved message. */
  hasOpen: boolean;
  /** Has at least one unacknowledged + unresolved message (= bold in list). */
  hasUnread: boolean;
}

const SEV_RANK: Record<string, number> = { critical: 3, warn: 2, info: 1 };

export function InboxPage() {
  const [includeResolved, setIncludeResolved] = useState(false);
  // Tracks whether the operator has explicitly clicked the checkbox.
  // Once they have, the auto-toggle effect must not override that
  // choice — otherwise unchecking immediately after the page auto-
  // toggled would loop back on (the selected thread is now invisible
  // → effect re-fires → flips back).
  const userToggledRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedThreadKey = searchParams.get("thread");
  const selectedAlertId = searchParams.get("id");
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["alerts", { includeResolved }],
    queryFn: () => alertsApi.list({ includeResolved }),
    refetchInterval: 30_000,
  });
  // Active per-(site, target_url) cooldowns from Owen's gate. Used
  // to badge content_brief alerts whose target page would be no-op
  // to execute right now (another keyword for the same page got
  // executed since this brief was filed).
  const cooldownsQ = useQuery({
    queryKey: ["optimize", "active-cooldowns"],
    queryFn: () => optimizeApi.activeCooldowns(),
    refetchInterval: 60_000,
  });
  // Doris's next tick — surfaces "retry in N min" on publish_signal
  // alerts so operator knows when the next auto-recovery sweep fires.
  const retryEtaQ = useQuery({
    queryKey: ["agents", "next-retry-eta"],
    queryFn: () => optimizeApi.nextRetryEta(),
    refetchInterval: 60_000,
  });

  const ackM = useMutation({
    mutationFn: (id: string) => alertsApi.ack(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const resolveM = useMutation({
    mutationFn: (id: string) => alertsApi.resolve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const executeM = useMutation({
    mutationFn: (id: string) => alertsApi.execute(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const reRunM = useMutation({
    mutationFn: (id: string) => alertsApi.reRun(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  const allAlerts = data?.alerts ?? [];

  // Group alerts into threads. Alerts sharing threadKey collapse;
  // alerts without threadKey become singleton threads keyed on their
  // dedup_key so the list pane can render them uniformly.
  const threads = useMemo(() => groupIntoThreads(allAlerts), [allAlerts]);

  // Resolve the selected thread. URL precedence:
  //   1. ?thread=<key> — direct
  //   2. ?id=<alertId>   — find the thread containing that alert
  //   3. (none)        — first thread in the list
  const selectedThread = useMemo(() => {
    if (selectedThreadKey) {
      return threads.find((t) => t.key === selectedThreadKey) ?? null;
    }
    if (selectedAlertId) {
      return threads.find((t) => t.messages.some((m) => m.id === selectedAlertId)) ?? null;
    }
    return null;
  }, [threads, selectedThreadKey, selectedAlertId]);

  // Auto-select first thread on initial mount when no selection in URL.
  // ONLY on desktop (≥ lg breakpoint = 1024px). On mobile this used to
  // shove operators straight into the first thread's detail view on
  // /inbox — they never saw the list, so a portfolio of 20 threads
  // looked like one. Operator could only navigate via the small "← Back"
  // button most missed. On mobile we let the list render first; user
  // taps a row to view detail. Standard mobile pattern (Gmail/Linear/etc).
  useEffect(() => {
    if (selectedThreadKey || selectedAlertId) return;
    if (threads.length === 0) return;
    // Skip auto-select on mobile — list-first is the desired UX.
    if (typeof window !== "undefined" && !window.matchMedia("(min-width: 1024px)").matches) return;
    if (threads[0]) {
      const next = new URLSearchParams(searchParams);
      next.set("thread", threads[0].key);
      setSearchParams(next, { replace: true });
    }
  }, [selectedThreadKey, selectedAlertId, threads, searchParams, setSearchParams]);

  // Auto-toggle "Show resolved" when the URL points to a thread/alert
  // not in the current list. Mirrors the pre-thread behavior so deep
  // links to resolved threads still work. Once the operator has
  // explicitly toggled the checkbox, we respect that — otherwise
  // un-checking with a now-invisible thread selected would loop
  // straight back on.
  useEffect(() => {
    if (userToggledRef.current) return;
    if (!selectedThreadKey && !selectedAlertId) return;
    if (selectedThread) return;
    if (includeResolved) return;
    if (isLoading) return;
    setIncludeResolved(true);
  }, [selectedThreadKey, selectedAlertId, selectedThread, includeResolved, isLoading]);

  const selectThread = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("thread", key);
    next.delete("id"); // ?thread is the canonical selector
    setSearchParams(next, { replace: false });
  };
  const clearSelection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("thread");
    next.delete("id");
    setSearchParams(next, { replace: true });
  };

  if (isLoading) return <div className="text-sm text-ink-500">Loading inbox…</div>;
  if (error) return <div className="text-sm text-red-600">Error: {String(error)}</div>;
  if (!data) return null;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-display font-bold text-ink-900">Issue Inbox</h1>
        <div className="flex items-center gap-2 text-xs">
          <CountChip label="Critical" value={data.counts.critical} color="red" />
          <CountChip label="Warn" value={data.counts.warn} color="amber" />
          <CountChip label="Info" value={data.counts.info} color="ink" />
          <label className="flex items-center gap-1.5 ml-3">
            <input
              type="checkbox"
              checked={includeResolved}
              onChange={(e) => {
                userToggledRef.current = true;
                setIncludeResolved(e.target.checked);
                // If un-checking and the currently-selected thread is
                // resolved-only, drop the URL selection so the right
                // pane clears instead of being stuck on a now-invisible
                // thread.
                if (!e.target.checked && selectedThread && !selectedThread.hasOpen) {
                  const next = new URLSearchParams(searchParams);
                  next.delete("thread");
                  next.delete("id");
                  setSearchParams(next, { replace: true });
                }
              }}
              className="rounded"
            />
            <span className="text-ink-600">Show resolved</span>
          </label>
        </div>
      </div>

      {threads.length === 0 ? (
        <div className="text-center py-12 text-ink-500 border border-dashed border-ink-200 rounded-lg">
          {includeResolved
            ? "No alerts on record."
            : "🎉 No open issues. Everything is healthy."}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 items-start">
          {/* List pane — thread rows.
           *
           * Height constraint is `lg:`-gated. On desktop the list is a
           * fixed-height scrollable column next to the detail pane.
           * On mobile, no max-h: the list flows naturally and the
           * PAGE scrolls. Without this gate (the prior implementation),
           * mobile users saw a small bounded box with internal touch-
           * scroll that's easy to miss — read as "only one message
           * exists" even when the thread list had 20+. */}
          <div className={"bg-white rounded-xl border border-ink-100 overflow-hidden " + (selectedThread ? "hidden lg:block" : "block")}>
            <ul className="divide-y divide-ink-100 lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto">
              {threads.map((t) => (
                <ThreadRow
                  key={t.key}
                  thread={t}
                  selected={t.key === selectedThread?.key}
                  onSelect={() => selectThread(t.key)}
                />
              ))}
            </ul>
          </div>

          {/* Detail pane — full thread */}
          <div className={"bg-white rounded-xl border border-ink-100 " + (selectedThread ? "block" : "hidden lg:block")}>
            {selectedThread ? (
              <ThreadDetail
                thread={selectedThread}
                onBack={clearSelection}
                onAck={(id) => ackM.mutate(id)}
                onResolve={(id) => resolveM.mutate(id)}
                onExecute={(id) => executeM.mutate(id)}
                onReRun={(id) => reRunM.mutate(id)}
                busy={ackM.isPending || resolveM.isPending || executeM.isPending || reRunM.isPending}
                executeError={executeM.error ? String((executeM.error as Error).message) : null}
                executeData={executeM.data ?? null}
                reRunData={reRunM.data ?? null}
                reRunError={reRunM.error ? String((reRunM.error as Error).message) : null}
                cooldowns={cooldownsQ.data?.cooldowns ?? []}
                dorisNextRunAt={retryEtaQ.data?.doris?.nextRunAt ?? null}
              />
            ) : (
              <div className="p-12 text-center text-sm text-ink-500">
                Select a thread from the list to view its messages.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Group flat alert list into threads. Alerts sharing threadKey
 *  collapse into one thread; alerts without threadKey become singleton
 *  threads keyed by dedup_key. Thread sort: most-recent activity first. */
function groupIntoThreads(alerts: Alert[]): Thread[] {
  const byKey = new Map<string, Alert[]>();
  for (const a of alerts) {
    const key = a.threadKey ?? `solo|${a.dedupKey}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(a);
  }
  const threads: Thread[] = [];
  for (const [key, msgs] of byKey) {
    // Oldest-first within thread (chronological reading order).
    const sorted = [...msgs].sort((a, b) =>
      new Date(a.raisedAt).getTime() - new Date(b.raisedAt).getTime(),
    );
    const newest = sorted[sorted.length - 1]!;
    const subject = sorted[0]!.title;
    const hasOpen = sorted.some((m) => !m.resolvedAt);
    const hasUnread = sorted.some((m) => !m.acknowledgedAt && !m.resolvedAt);
    const severity = sorted.reduce<"critical" | "warn" | "info">(
      (acc, m) => (SEV_RANK[m.severity]! > SEV_RANK[acc]! ? m.severity : acc),
      "info",
    );
    threads.push({
      key,
      subject,
      category: newest.category,
      domain: newest.domain,
      siteId: newest.siteId,
      messages: sorted,
      lastActivity: newest.raisedAt,
      severity,
      hasOpen,
      hasUnread,
    });
  }
  // Sort: most-recent activity first.
  threads.sort(
    (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime(),
  );
  return threads;
}

function ThreadRow({
  thread,
  selected,
  onSelect,
}: {
  thread: Thread;
  selected: boolean;
  onSelect: () => void;
}) {
  const sevBar =
    thread.severity === "critical" ? "bg-red-500"
    : thread.severity === "warn" ? "bg-amber-500"
    : "bg-ink-300";
  const isResolved = !thread.hasOpen;
  const newest = thread.messages[thread.messages.length - 1]!;
  // Preview = newest message's title or first line of body — gives the
  // operator a sense of "what just happened in this thread."
  const preview = newest.description ? markdownToPreview(newest.description, 100) : newest.title;
  const msgCount = thread.messages.length;
  // Show the LATEST agent's emoji + name in the row eyebrow so
  // operator can scan "who posted last" (e.g. brief from Owen, then
  // grade from Grace later — the row icon shifts to 📝 / Grace).
  const latestAgent = labelForCategory(newest.category);

  return (
    <li>
      <button
        onClick={onSelect}
        className={
          "w-full flex items-stretch text-left transition-colors " +
          (selected ? "bg-ink-100" : "hover:bg-ink-50") +
          (isResolved ? " opacity-60" : "")
        }
      >
        <div className={`w-1 ${sevBar} shrink-0`} />
        <div className="flex-1 min-w-0 px-3 py-2.5">
          <div className="flex items-baseline gap-2 text-[10px] text-ink-500 uppercase tracking-wide mb-0.5">
            <span className="font-bold">{thread.severity}</span>
            <span>·</span>
            <span className="normal-case text-ink-700 font-medium truncate">
              <span className="mr-0.5">{latestAgent.emoji}</span>{latestAgent.label}
            </span>
            {msgCount > 1 && (
              <>
                <span>·</span>
                <span className="text-accent font-semibold normal-case">{msgCount} msgs</span>
              </>
            )}
            {thread.domain && (
              <>
                <span>·</span>
                <span className="truncate text-ink-700 font-medium normal-case">{thread.domain}</span>
              </>
            )}
            <span className="ml-auto whitespace-nowrap text-ink-400">{formatTime(thread.lastActivity)}</span>
          </div>
          <div className={"text-sm text-ink-900 truncate " + (thread.hasUnread ? "font-semibold" : "font-normal")}>
            {thread.subject}
          </div>
          {preview && (
            <div className="text-xs text-ink-500 truncate mt-0.5">{preview}</div>
          )}
        </div>
      </button>
    </li>
  );
}

function ThreadDetail({
  thread,
  onBack,
  onAck,
  onResolve,
  onExecute,
  onReRun,
  busy,
  executeError,
  executeData,
  reRunData,
  reRunError,
  cooldowns,
  dorisNextRunAt,
}: {
  thread: Thread;
  onBack: () => void;
  onAck: (id: string) => void;
  onResolve: (id: string) => void;
  onExecute: (id: string) => void;
  onReRun: (id: string) => void;
  busy: boolean;
  executeError: string | null;
  executeData: { executionUrl?: string; alreadyDispatched?: boolean } | null;
  reRunData: { runsUrl?: string; domain?: string; keyword?: string } | null;
  reRunError: string | null;
  cooldowns: ActiveCooldown[];
  dorisNextRunAt: string | null;
}) {
  // Action buttons live on the LATEST OPEN message — that's the one
  // the operator can still act on. Resolved messages are read-only.
  const latestOpen = [...thread.messages].reverse().find((m) => !m.resolvedAt);
  const sevColor =
    thread.severity === "critical" ? "bg-red-100 text-red-800"
    : thread.severity === "warn" ? "bg-amber-100 text-amber-800"
    : "bg-ink-100 text-ink-700";

  return (
    // Height constraint `lg:`-gated. On desktop the detail pane is a
    // fixed-height column with its own internal scroll for messages.
    // On mobile, no max-h — the page scrolls naturally as the user
    // reads through messages, which is the standard mobile pattern.
    // Without this gate, mobile users saw a small bounded box with
    // internal touch-scroll inside the messages list (often missed).
    <div className="flex flex-col lg:max-h-[calc(100vh-180px)]">
      {/* Thread header */}
      <div className="px-5 py-4 border-b border-ink-100">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={onBack}
            // Mobile-only (`lg:hidden`). Sized for thumb-tap — 44px min
            // height per Apple HIG. Was previously text-xs px-2 py-1
            // (~24-28px tall), too small to find/hit reliably on phones.
            className="lg:hidden inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 -ml-1 rounded-md hover:bg-ink-100 active:bg-ink-200 text-accent min-h-[44px]"
            aria-label="Back to thread list"
          >
            <span aria-hidden className="text-base leading-none">←</span>
            <span>Inbox</span>
          </button>
          <span className={`uppercase tracking-wide text-[10px] font-semibold px-1.5 py-0.5 rounded ${sevColor}`}>
            {thread.severity}
          </span>
          <span className="text-xs text-ink-500">{thread.messages.length} message{thread.messages.length === 1 ? "" : "s"}</span>
          <span className="text-xs text-ink-400 ml-auto">Latest {new Date(thread.lastActivity).toLocaleString()}</span>
        </div>
        <h2 className="text-lg font-display font-bold text-ink-900 leading-snug">{thread.subject}</h2>
        {thread.domain && (
          <Link
            to={`/sites/${thread.siteId}`}
            className="inline-block mt-1 text-sm text-accent hover:underline"
          >
            {thread.domain} →
          </Link>
        )}
      </div>

      {/* Messages — overflow-y-auto only on desktop where the parent
       *  is a fixed-height column. On mobile the parent has no max-h
       *  so this just flows. */}
      <div className="px-5 py-4 lg:overflow-y-auto flex-1 space-y-4">
        {thread.messages.map((m, i) => (
          <MessageCard
            key={m.id}
            message={m}
            isLatest={i === thread.messages.length - 1}
          />
        ))}
      </div>

      {/* Footer with actions on the latest open message */}
      {latestOpen && (
        <div className="px-5 py-3 border-t border-ink-100 bg-ink-50 flex flex-col gap-2">
          <ContextualBanner
            alert={latestOpen}
            cooldowns={cooldowns}
            dorisNextRunAt={dorisNextRunAt}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-ink-500 mr-2">Actions on latest open message:</span>
            {latestOpen.acknowledgedAt ? (
              <span className="text-xs text-ink-500 italic">
                Acknowledged {new Date(latestOpen.acknowledgedAt).toLocaleString()}
              </span>
            ) : (
              <button
                onClick={() => onAck(latestOpen.id)}
                disabled={busy}
                className="text-sm px-3 py-1.5 rounded border border-ink-200 hover:bg-white text-ink-700 disabled:opacity-50"
              >
                Acknowledge
              </button>
            )}
            {/* Execute button — ALWAYS visible when alert has an
                executable, regardless of executed_at state. Previously
                we hid the button after dispatch, but that left the
                operator stuck with no retry path when a workflow
                failed (saw 5 briefs fail audit on 2026-05-08; UI gave
                only "Running on GH Actions ↗" link, no way to re-fire).
                The button text adapts: ▶ Execute on first dispatch,
                ↻ Re-Execute when a previous run exists.
                Disabled only while a click is in flight (busy). */}
            {latestOpen.executable && (
              <button
                onClick={() => onExecute(latestOpen.id)}
                disabled={busy}
                className="text-sm px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                title={`Dispatches inbox-action.yml on GH Actions with kind=${latestOpen.executable.kind}${latestOpen.executedAt ? ' — re-runs the previously-dispatched workflow' : ''}`}
              >
                {busy
                  ? "Dispatching…"
                  : latestOpen.executable.kind === "retry-publish-signals"
                    ? (latestOpen.executionUrl ? "↻ Re-Retry" : "↻ Retry")
                    : (latestOpen.executionUrl ? "↻ Re-Execute" : "▶ Execute")}
              </button>
            )}
            {/* GH Actions deep link — informational only. Shown
                whenever an execution_url exists, regardless of
                executed_at state. The Execute/Re-Execute button
                above is the action; this is the navigational
                breadcrumb to the most-recent run's logs. */}
            {latestOpen.executionUrl && (
              <a
                href={latestOpen.executionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2 py-1 rounded border border-ink-200 hover:bg-ink-50 text-ink-600 inline-flex items-center gap-1"
                title={latestOpen.executedAt
                  ? `Last dispatched ${new Date(latestOpen.executedAt).toLocaleString()} — click to view the GH Actions run`
                  : "Click to view the most-recent GH Actions run for this alert"}
              >
                <span>Last run ↗</span>
              </a>
            )}
            {/* "Last run" link consolidated into the unified block
                above (regardless of executed_at state). This branch
                kept blank to preserve the spacing between the
                executable buttons and the optimize Re-Run button. */}
            {/* Re-Run optimize — for grade / execution_report alerts in
                a keyword thread. Re-fires the optimize workflow without
                needing the original brief alert (which is typically
                already resolved by the time the operator wants to retry). */}
            {(latestOpen.category === "grade" || latestOpen.category === "execution_report") &&
              latestOpen.threadKey?.startsWith("keyword|") && (
              <button
                onClick={() => onReRun(latestOpen.id)}
                disabled={busy}
                className="text-sm px-3 py-1.5 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                title="Re-run the optimize workflow for this (site, keyword). Useful when the regen got a low grade — Connie's brief-coverage validator will retry until all H2s land verbatim."
              >
                {busy ? "Dispatching…" : "↻ Re-Run optimize"}
              </button>
            )}
            <button
              onClick={() => onResolve(latestOpen.id)}
              disabled={busy}
              className="text-sm px-3 py-1.5 rounded bg-ink-900 text-white hover:bg-ink-700 disabled:opacity-50 ml-auto"
            >
              Resolve
            </button>
          </div>
          {executeError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              Execute failed: {executeError}
            </div>
          )}
          {executeData?.alreadyDispatched && (
            <div className="text-xs text-ink-500 italic">
              Already dispatched — use the GH Actions link above.
            </div>
          )}
          {reRunError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              Re-Run failed: {reRunError}
            </div>
          )}
          {reRunData?.runsUrl && (
            <div className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-2 py-1 flex items-center gap-2">
              <span>Re-Run dispatched for <strong>{reRunData.domain}</strong> · "{reRunData.keyword}".</span>
              <a href={reRunData.runsUrl} target="_blank" rel="noopener noreferrer" className="underline">Watch run ↗</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MessageCard({ message, isLatest }: { message: Alert; isLatest: boolean }) {
  const isResolved = Boolean(message.resolvedAt);
  // Map agent category to a display emoji + label. The execution_report
  // category is filed by the workflow, not an agent — call it "Run".
  const { emoji, label } = labelForCategory(message.category);
  return (
    <div
      className={
        "border border-ink-100 rounded-lg overflow-hidden " +
        (isLatest ? "ring-2 ring-accent/20 shadow-sm" : "") +
        (isResolved ? " opacity-70" : "")
      }
    >
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-100 flex items-center gap-2 text-xs">
        <span>{emoji}</span>
        <span className="font-semibold text-ink-900">{label}</span>
        <span className="text-ink-400 ml-auto">{new Date(message.raisedAt).toLocaleString()}</span>
      </div>
      <div className="px-4 py-3">
        <h3 className="text-sm font-semibold text-ink-900 mb-2">{message.title}</h3>
        {message.description ? (
          <MarkdownBody source={message.description} />
        ) : (
          <p className="text-xs text-ink-500 italic">No body.</p>
        )}
        {isResolved && (
          <div className="mt-3 text-[11px] text-ink-500 italic">
            Resolved {new Date(message.resolvedAt!).toLocaleString()}
            {message.resolutionNote ? ` — ${message.resolutionNote}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

function labelForCategory(category: string): { emoji: string; label: string } {
  switch (category) {
    // Owen + the optimize loop
    case "content_brief": return { emoji: "🎯", label: "Owen — content brief" };
    case "optimize_queue": return { emoji: "🎯", label: "Owen — regen ready" };
    case "execution_report": return { emoji: "🚀", label: "Run — execution report" };
    case "grade": return { emoji: "📝", label: "Grace — grade" };
    case "deploy_queue": return { emoji: "🎯", label: "Owen — deploy queue" };
    // Validation + audit
    case "audit": return { emoji: "🛡️", label: "Officer Kim — audit" };
    case "validation_summary": return { emoji: "🛡️", label: "Connie — validation" };
    // Availability + monitoring
    case "availability": return { emoji: "🔔", label: "Walt — availability" };
    case "site_health": return { emoji: "🔔", label: "Walt — health check" };
    case "lead_form": return { emoji: "📞", label: "Lead webhook" };
    // Indexing + publish signals
    case "indexnow": return { emoji: "📨", label: "Doris — IndexNow" };
    case "publish_signal": return { emoji: "📨", label: "Doris — publish signal" };
    case "sitemap": return { emoji: "📨", label: "Doris — sitemap" };
    // Onboarding / setup
    case "onboarding": return { emoji: "🛠️", label: "Onboarding" };
    case "ga4_setup": return { emoji: "📊", label: "GA4 setup" };
    case "search_console": return { emoji: "🔍", label: "Search Console" };
    // Competitor research
    case "competitor_research": return { emoji: "🕵️", label: "Spencer — competitor probe" };
    case "competitor_portfolio": return { emoji: "🕵️", label: "Spencer — portfolio rollup" };
    // Cost + cron
    case "cost_alert": return { emoji: "💰", label: "Penny — cost" };
    case "cron_failure": return { emoji: "⏰", label: "Cron failure" };
    // Misc / unknown — show the raw category for debugging
    default: return { emoji: "ℹ", label: category };
  }
}

function CountChip({ label, value, color }: { label: string; value: number; color: "red" | "amber" | "ink" }) {
  const cls =
    color === "red" ? "bg-red-100 text-red-700"
    : color === "amber" ? "bg-amber-100 text-amber-800"
    : "bg-ink-100 text-ink-700";
  return (
    <span className={`px-2 py-0.5 rounded font-medium ${cls}`}>
      {value} {label}
    </span>
  );
}

// Strip basic markdown to produce a one-line snippet for the list view.
function markdownToPreview(md: string, max: number): string {
  const text = md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]+`/g, " ")
    .replace(/^>\s*/gm, "")
    .replace(/^#+\s*/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max - 1).trimEnd() + "…" : text;
}

/**
 * Inline warning rendered just above the action buttons. Surfaces
 * blocking conditions the operator should know about BEFORE clicking
 * Execute/Retry — e.g. "page already mutated 3 days ago, cooldown
 * has 11 days left" for content_brief alerts, or "Doris will retry
 * automatically in 12 min" for publish_signal alerts.
 */
function ContextualBanner({
  alert,
  cooldowns,
  dorisNextRunAt,
}: {
  alert: Alert;
  cooldowns: ActiveCooldown[];
  dorisNextRunAt: string | null;
}) {
  // content_brief — show page-cooldown warning when applicable. The
  // alert's executable carries domain + siteId; v1 always targets
  // homepage so target_url = `https://${domain}/`.
  if (alert.category === "content_brief" && alert.executable && alert.siteId) {
    const exec = alert.executable as { domain?: unknown };
    const domain = typeof exec.domain === "string" ? exec.domain : null;
    if (domain) {
      const targetUrl = `https://${domain}/`;
      const match = cooldowns.find((c) => c.siteId === alert.siteId && c.targetUrl === targetUrl);
      if (match && new Date(match.expiresAt).getTime() > Date.now()) {
        const daysLeft = Math.max(1, Math.ceil((new Date(match.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
        return (
          <div className="text-xs bg-purple-50 border border-purple-200 rounded px-2.5 py-1.5 text-purple-800">
            <strong>Page cooldown active.</strong>{" "}
            The homepage of {domain} was rewritten on{" "}
            {new Date(match.lastAttemptAt).toLocaleDateString()} — Owen's 14-day cooldown has{" "}
            <strong>{daysLeft}d</strong> left. Executing now would overwrite that change before Spencer can measure its rank impact.
          </div>
        );
      }
    }
  }
  // publish_signal — show Doris next-retry timing.
  if (alert.category === "publish_signal" && dorisNextRunAt) {
    const ms = new Date(dorisNextRunAt).getTime() - Date.now();
    const dueLabel = ms <= 0 ? "any moment now" : ms < 60_000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60_000)}m`;
    return (
      <div className="text-xs bg-blue-50 border border-blue-200 rounded px-2.5 py-1.5 text-blue-800">
        <strong>Doris will auto-retry</strong> publish-signal failures on her next tick — about <strong>{dueLabel}</strong>. You can still click Retry to dispatch immediately.
      </div>
    );
  }
  return null;
}

function formatTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 60) return min < 1 ? "now" : `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
